import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { Button } from "./ui/button";
import {
  isPushSupported,
  isPromptDismissed,
  dismissPrompt,
  requestPermissionAndSubscribe,
  trySilentAutoSubscribe,
} from "../lib/pushManager";

/**
 * Top-of-dashboard banner that nudges users to enable push notifications.
 *
 * Shows when the browser supports push AND the user isn't currently
 * receiving pushes on this device. That means one of:
 *   - permission is still 'default' (never asked / never decided), OR
 *   - permission is 'granted' but the server has no push_subscription
 *     for this user (they cleared browser data, changed device, or the
 *     subscription was cleared server-side after a 404/410 from the push
 *     service). Before iter-158 the banner missed this second case and
 *     silently hid, which is why so many members "never enabled" push
 *     even though they had granted permission in the past.
 *
 * The banner respects a 14-day dismissal so it doesn't nag every visit.
 */
export const PushPermissionBanner = () => {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) return;
      if (isPromptDismissed()) return;

      const perm = Notification.permission;
      if (perm === "denied") return;

      if (perm === "default") {
        if (!cancelled) setVisible(true);
        return;
      }

      // permission === "granted" — check the server. If the server has
      // no subscription for us, try a silent re-subscribe first (may
      // succeed instantly because the OS-level permission is still on);
      // if that fails, show the banner so the user can retry with a
      // gesture (some browsers require it).
      try {
        const { data } = await apiClient.get("/push/status");
        if (data?.subscribed) return;
      } catch {
        return; // couldn't reach server — don't nag
      }

      const ok = await trySilentAutoSubscribe();
      if (!cancelled && !ok) setVisible(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!visible) return null;

  const handleEnable = async () => {
    setBusy(true);
    const status = await requestPermissionAndSubscribe();
    setBusy(false);
    if (status === "granted") {
      toast.success("Push notifications enabled — you'll get instant alerts for new messages and events");
      setVisible(false);
    } else if (status === "denied") {
      toast.error("Permission denied. You can re-enable it later from your browser settings.");
      setVisible(false);
    } else {
      toast.error("Could not enable notifications. Try again from Profile → Push notifications.");
    }
  };

  const handleDismiss = () => {
    dismissPrompt();
    setVisible(false);
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-r from-primary/[0.08] to-primary/[0.02] p-4 sm:p-5"
      data-testid="push-permission-banner"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center">
          <Bell className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm sm:text-base">Get instant notifications</p>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 break-words">
            Get a ping for new chat messages, event reminders and committee notices — even when the app is closed.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              size="sm"
              onClick={handleEnable}
              disabled={busy}
              data-testid="push-banner-enable-btn"
              className="h-8"
            >
              {busy ? "Enabling…" : "Enable now"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              disabled={busy}
              data-testid="push-banner-dismiss-btn"
              className="h-8"
            >
              Maybe later
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-white/5"
          aria-label="Dismiss"
          data-testid="push-banner-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
